import { Middleware } from '@reduxjs/toolkit';
import { showInfo, showWarning, showError, showSuccess } from 'utilities/toastManager';
import { distanceToString } from 'utilities/format';

/**
 * Toast Middleware
 *
 * Watches for _toastMessage property in state and displays toast notifications.
 * Reducers can set state._toastMessage to trigger a toast.
 *
 * A reducer that needs a properly unit-formatted distance in its message can't format it
 * itself — a plain `createSlice` reducer only has its own slice's draft state, not
 * `state.ui.units` (a sibling slice). Instead it sets `state._toastHeightMeters` (the raw
 * metres value) alongside a `{height}` placeholder in `_toastMessage`; THIS middleware has
 * `getState()` (full root state, `state.ui` included), so it substitutes the placeholder with
 * `distanceToString` in the user's actual display unit right before showing the toast.
 *
 * A SECOND, independent toast can also land in `_toastMessage2` / `_toastSeverity2` /
 * `_toastHeightMeters2` — see `setOrQueueToast` in room.ts. This happens when two unrelated
 * things are both true on the same dispatch (e.g. a platform-switch notice AND an
 * acoustics-limited-height notice) and neither should be dropped in favor of the other.
 * `utilities/toastManager.ts` already queues distinct messages and shows them one after
 * another, so calling show() for both here surfaces both toasts to the user in sequence.
 */
const showToastSlot = (state: any, message: string, heightMeters: unknown, severity: string | undefined) => {
  let resolved = message;
  if (resolved.includes('{height}') && typeof heightMeters === 'number') {
    const isMeter = state.ui?.units === 'meters';
    resolved = resolved.replace('{height}', distanceToString(heightMeters, isMeter));
  }
  const show =
    severity === 'warning' ? showWarning :
    severity === 'error'   ? showError   :
    severity === 'success' ? showSuccess :
                             showInfo;
  console.log('🍞 Toast middleware: Showing message:', resolved);
  show(resolved);
};

const toastMiddleware: Middleware = ({ getState }) => next => action => {
  // Let the action pass through first
  const result = next(action);

  // Check if state has a toast message to display
  const state = getState();
  const container = state.container;
  const hasPrimary = Boolean(container?._toastMessage);
  const hasSecondary = Boolean(container?._toastMessage2);

  if (hasPrimary) {
    showToastSlot(state, container._toastMessage, container._toastHeightMeters, container._toastSeverity);
  }
  if (hasSecondary) {
    showToastSlot(state, container._toastMessage2, container._toastHeightMeters2, container._toastSeverity2);
  }

  if (hasPrimary || hasSecondary) {
    next({
      type: 'container/clearToastMessage',
      payload: undefined
    });
  }

  return result;
};

export default toastMiddleware;
